import { roleProfile } from "../sales/roleProfiles";
export const workPhases = [
  "production",
  "ready",
  "delivery",
  "installation",
  "reclamation",
] as const;
export type WorkPhase = (typeof workPhases)[number];
export const phaseLabels: Record<WorkPhase | "completed", string> = {
  production: "Производство",
  ready: "Готов",
  delivery: "Доставка",
  installation: "Монтаж",
  reclamation: "Рекламация",
  completed: "Завершено",
};
export type WorkFile = {
  path: string;
  name: string;
  type: string;
  size: number;
};
export type WorkStep = {
  id: string;
  phase: WorkPhase;
  title: string;
  checklist: string[];
  photoRequired: boolean;
  deadline?: string;
  attempt: number;
  status: "active" | "pending" | "approved";
  correctionFor?: string;
};
export type WorkEvent = {
  id: string;
  at: string;
  actor: number;
  actorName: string;
  action: string;
  step?: string;
  attempt?: number;
  text: string;
  checks?: boolean[];
  files?: WorkFile[];
  done?: boolean;
};
export type WorkState = {
  version: 1;
  launched: boolean;
  paused: boolean;
  steps: WorkStep[];
  events: WorkEvent[];
};
export type WorkProject = {
  calendar_color?: string | null;
  stage_map?: Record<string, string>;
  id: number;
  name: string;
  description: string;
  deadline: string | null;
  pipeline: string;
  stage: string;
  manager_id: number | null;
  revision: number;
  archived: boolean;
  state: WorkState;
  files: WorkFile[];
};
export type WorkActor = {
  id: number;
  name: string;
  admin: boolean;
  role?: string;
  disabled?: boolean;
};
export type WorkAction = {
  type:
    | "setup"
    | "launch"
    | "report"
    | "submit"
    | "approve"
    | "reject"
    | "reset"
    | "correction"
    | "pause"
    | "resume";
  text?: string;
  done?: boolean;
  checks?: boolean[];
  files?: WorkFile[];
  manager?: number | null;
  description?: string;
  steps?: WorkStep[];
};
export const currentWorkStep = (p: WorkProject) =>
  p.state.steps.find((s) => s.status !== "approved");
export const latestStepDecision = (p: WorkProject, step: WorkStep) =>
  [...p.state.events]
    .reverse()
    .find(
      (e) =>
        e.step === step.id &&
        e.attempt === step.attempt - 1 &&
        ["reject", "reset", "correction"].includes(e.action),
    );
export function reportDraftChecks(p: WorkProject, step: WorkStep) {
  const decision = latestStepDecision(p, step);
  const previous =
    decision?.action === "reject"
      ? [...p.state.events]
          .reverse()
          .find(
            (e) =>
              e.step === step.id &&
              e.attempt === decision.attempt &&
              e.action === "report",
          )
      : undefined;
  return step.checklist.map((_, i) => previous?.checks?.[i] || false);
}
export function canReadWork(p: WorkProject, actor: WorkActor) {
  return (
    !actor.disabled &&
    (actor.admin ||
      ((roleProfile(actor.role)?.projects ?? actor.role === "manager") &&
        p.manager_id === actor.id))
  );
}
export function defaultRoadmap(): WorkState {
  const groups: [WorkPhase, string, string[]][] = [
    [
      "production",
      "Приём проекта в производство",
      [
        "Получены актуальные чертежи",
        "Состав работ и открытые вопросы проверены",
      ],
    ],
    [
      "production",
      "Контроль материалов и фурнитуры",
      [
        "Материалы соответствуют документации",
        "Комплектация и сроки поставки проверены",
      ],
    ],
    [
      "production",
      "Контроль изготовления",
      ["Размеры и обработка проверены", "Замечания переданы производству"],
    ],
    [
      "production",
      "Контроль отделки и покраски",
      ["Цвет и качество отделки проверены", "Фото результата приложены"],
    ],
    [
      "production",
      "Предварительная приёмка",
      ["Проверены внешний вид и геометрия", "Контрольная сборка выполнена"],
    ],
    [
      "ready",
      "Комплектность и упаковка",
      [
        "Все изделия и фурнитура в комплекте",
        "Упаковка и маркировка проверены",
      ],
    ],
    [
      "ready",
      "Готовность объекта и бригады",
      [
        "Доступ на объект согласован",
        "Документация и состав бригады проверены",
      ],
    ],
    [
      "delivery",
      "Передача перевозчику",
      ["Количество мест зафиксировано", "Состояние упаковки зафиксировано"],
    ],
    [
      "delivery",
      "Приёмка доставки",
      ["Груз доставлен в полном объёме", "Повреждения проверены и описаны"],
    ],
    [
      "installation",
      "Разметка и скрытые работы",
      ["Крепления и основания проверены", "Фото скрытых работ приложены"],
    ],
    [
      "installation",
      "Сборка и регулировка",
      [
        "Геометрия и устойчивость проверены",
        "Фасады, ящики и механизмы проверены",
      ],
    ],
    [
      "installation",
      "Сдача и замечания",
      [
        "Работа изделий продемонстрирована",
        "Все замечания заказчика зафиксированы",
      ],
    ],
    [
      "reclamation",
      "Закрытие замечаний и завершение",
      [
        "Рекламации устранены либо подтверждено их отсутствие",
        "Итоговая приёмка подтверждена",
      ],
    ],
  ];
  return {
    version: 1,
    launched: false,
    paused: false,
    events: [],
    steps: groups.map(([phase, title, checklist], i) => ({
      id: `step-${i + 1}`,
      phase,
      title,
      checklist,
      photoRequired: true,
      attempt: 1,
      status: "active",
    })),
  };
}
export function validateRoadmap(steps: WorkStep[]) {
  if (!Array.isArray(steps) || steps.length < 5 || steps.length > 100)
    throw new Error("Добавьте от 5 до 100 контрольных точек");
  let phase = -1;
  const ids = new Set<string>();
  for (const s of steps) {
    const index = workPhases.indexOf(s.phase);
    if (
      index < phase ||
      index < 0 ||
      !s.id ||
      ids.has(s.id) ||
      !s.title?.trim() ||
      s.title.length > 250 ||
      !Array.isArray(s.checklist) ||
      !s.checklist.length ||
      s.checklist.length > 30 ||
      s.checklist.some((c) => !c.trim() || c.length > 500)
    )
      throw new Error("Проверьте порядок этапов, названия и чек-листы");
    if (
      s.deadline &&
      (!/^\d{4}-\d{2}-\d{2}$/.test(s.deadline) ||
        new Date(s.deadline).toISOString().slice(0, 10) !== s.deadline)
    )
      throw new Error("Некорректный срок контрольной точки");
    ids.add(s.id);
    phase = index;
  }
  if (workPhases.some((p) => !steps.some((s) => s.phase === p)))
    throw new Error("Сохраните все этапы от производства до рекламации");
}
export function applyWorkAction(
  project: WorkProject,
  actor: WorkActor,
  revision: number,
  action: WorkAction,
  now = new Date().toISOString(),
): WorkProject {
  if (!canReadWork(project, actor)) throw new Error("Нет доступа к проекту");
  if (project.archived) throw new Error("Проект находится в архиве");
  if (revision !== project.revision)
    throw new Error("Проект изменён. Обновите страницу");
  const p = structuredClone(project),
    state = p.state,
    step = currentWorkStep(p);
  const adminActions = [
    "setup",
    "launch",
    "approve",
    "reject",
    "reset",
    "correction",
    "pause",
    "resume",
  ];
  if (adminActions.includes(action.type) && !actor.admin)
    throw new Error("Действие доступно только руководителю");
  if (state.steps.every((s) => s.status === "approved"))
    throw new Error("Проект завершён");
  const text = action.text?.trim() || "";
  if (text.length > 10000) throw new Error("Пояснение слишком длинное");
  const event: WorkEvent = {
    id: crypto.randomUUID(),
    at: now,
    actor: actor.id,
    actorName: actor.name,
    action: action.type,
    text,
    step: step?.id,
    attempt: step?.attempt,
  };
  if (action.type === "setup") {
    if (action.manager !== undefined) p.manager_id = action.manager;
    if (action.description !== undefined)
      p.description = action.description.slice(0, 10000);
    if (action.files) p.files = action.files;
    if (action.steps) {
      if (state.launched)
        throw new Error("После запуска используйте промежуточные исправления");
      validateRoadmap(action.steps);
      state.steps = action.steps.map((s) => ({
        ...s,
        status: "active",
        attempt: 1,
      }));
    }
  } else if (action.type === "launch") {
    if (state.launched) throw new Error("Дорожная карта уже запущена");
    if (p.manager_id == null) throw new Error("Назначьте менеджера");
    validateRoadmap(state.steps);
    state.launched = true;
  } else if (action.type === "pause" || action.type === "resume") {
    if (!state.launched || (action.type === "pause" && !text))
      throw new Error("Укажите причину приостановки запущенного проекта");
    state.paused = action.type === "pause";
  } else {
    if (!state.launched || state.paused || !step)
      throw new Error("Проект не запущен или приостановлен");
    if (["report", "submit"].includes(action.type) && actor.id !== p.manager_id)
      throw new Error("Отчёт отправляет назначенный менеджер");
    if (action.type === "report") {
      if (
        step.status !== "active" ||
        !text ||
        !Array.isArray(action.checks) ||
        action.checks.length !== step.checklist.length ||
        action.checks.some((c) => typeof c !== "boolean")
      )
        throw new Error("Заполните пояснение и чек-лист текущей точки");
      Object.assign(event, {
        checks: action.checks,
        files: action.files || [],
        done: !!action.done,
      });
    } else if (action.type === "submit") {
      const report = [...state.events]
        .reverse()
        .find(
          (e) =>
            e.action === "report" &&
            e.step === step.id &&
            e.attempt === step.attempt,
        );
      if (
        step.status !== "active" ||
        !report?.done ||
        !report.checks?.every(Boolean) ||
        (step.photoRequired &&
          !report.files?.some((f) => f.type.startsWith("image/")))
      )
        throw new Error("Нужен отчёт о выполнении со всеми пунктами и фото");
      step.status = "pending";
    } else if (action.type === "approve") {
      if (step.status !== "pending")
        throw new Error("Этап ещё не отправлен на проверку");
      step.status = "approved";
      const next = currentWorkStep(p);
      const phase = next?.phase || "completed";
      p.stage = p.stage_map?.[phase] || `${p.pipeline}-${phase}`;
    } else if (["reject", "reset", "correction"].includes(action.type)) {
      if (!text || (action.type !== "reset" && step.status !== "pending"))
        throw new Error("Укажите причину для отправленного на проверку этапа");
      step.status = "active";
      step.attempt++;
      if (action.type === "correction") {
        if (state.steps.length >= 100)
          throw new Error("Не более 100 контрольных точек");
        state.steps.splice(state.steps.indexOf(step), 0, {
          id: crypto.randomUUID(),
          title: `Исправление: ${text.slice(0, 150)}`,
          phase: step.phase,
          checklist: [text.slice(0, 500)],
          photoRequired: true,
          attempt: 1,
          status: "active",
          correctionFor: step.id,
        });
      }
    } else throw new Error("Неизвестное действие");
  }
  state.events.push(event);
  if (
    state.events.length > 3000 ||
    new TextEncoder().encode(JSON.stringify(state)).length > 1048576
  )
    throw new Error("Журнал проекта слишком большой");
  p.revision++;
  return p;
}
